import { Link } from "react-router-dom";
import { ArrowRight, GitCompare } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { translateEducation } from "@/lib/i18n";
import { MAP_PARTY_COLORS, NO_DATA_COLOR, formatRupees, partyLabel } from "@/lib/insights";

const Row = ({ k, v, testid }) => (
  <div className="flex justify-between gap-3 py-1.5 border-t border-[#f0ecdf] first:border-t-0">
    <dt className="text-[#6b6894]">{k}</dt>
    <dd className="font-semibold text-[#1a1640] text-right" data-testid={testid}>{v}</dd>
  </div>
);

export default function MlaCard({ seat, compact = false, onPick }) {
  const { t, lang } = useLang();
  const ta = lang === "ta";
  const m = seat.y2026;
  const color = MAP_PARTY_COLORS[m?.party_short] || NO_DATA_COLOR;
  const seatName = ta ? seat.name_ta : seat.name;

  if (compact) {
    return (
      <button onClick={() => onPick(seat.ac_no)} className="w-full text-left bg-white rounded-2xl p-3.5 flex items-center gap-3 hover:bg-[#fbf8ef] transition-colors" style={{ borderLeft: `5px solid ${color}` }} data-testid={`mla-mini-${seat.ac_no}`}>
        <div className="flex-1 min-w-0">
          <div className={`text-[14px] font-bold text-[#1a1640] truncate ${ta ? "font-tamil" : ""}`}>{m ? (ta ? m.mla_ta : m.mla) : t("not_listed")}</div>
          <div className={`text-[12px] text-[#6b6894] truncate ${ta ? "font-tamil" : ""}`}>{seatName}{m && ` · ${partyLabel(m.party_short, lang)}`}</div>
        </div>
        {m && <div className="text-[12px] font-semibold text-[#1a1640] tabular-nums whitespace-nowrap">{formatRupees(m.assets, t)}</div>}
      </button>
    );
  }

  return (
    <section className="bg-white rounded-[22px] p-5 sm:p-6 card-in" style={{ borderTop: `6px solid ${color}` }} data-testid="mla-card">
      <div className={`text-[11px] uppercase text-[#8c89ad] font-semibold ${ta ? "font-tamil" : "tracking-[0.2em]"}`}>{t("mla_2026")}</div>
      <h3 className={`mt-1 text-2xl font-semibold text-[#1a1640] ${ta ? "font-tamil" : "font-display"}`} data-testid="mla-card-name">{m ? (ta ? m.mla_ta : m.mla) : t("not_listed")}</h3>
      <div className={`text-[13px] text-[#6b6894] ${ta ? "font-tamil" : ""}`} data-testid="mla-card-seat">{seatName} · {ta ? seat.district_ta : seat.district} · AC {seat.ac_no}</div>
      {m && (
        <>
          <div className="mt-2 inline-flex items-center gap-1.5 text-[13px] font-bold text-[#1a1640]">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
            <span className={ta ? "font-tamil" : ""} data-testid="mla-card-party">{partyLabel(m.party_short, lang)}</span>
          </div>
          <dl className={`mt-4 text-[14px] ${ta ? "font-tamil" : ""}`}>
            <Row k={t("assets_label")} v={formatRupees(m.assets, t)} testid="mla-card-assets" />
            <Row k={t("liabilities")} v={formatRupees(m.liabilities, t)} testid="mla-card-liabilities" />
            <Row k={t("education")} v={translateEducation(m.education, lang)} testid="mla-card-education" />
            <Row k={t("cases_label")} v={m.cases ?? "—"} testid="mla-card-cases" />
          </dl>
        </>
      )}
      <div className="mt-5 flex flex-wrap gap-2">
        <Link to={`/changes?seat=${seat.ac_no}`} className={`inline-flex items-center gap-2 rounded-full bg-[#1a1640] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[#2a2170] transition-colors ${ta ? "font-tamil" : ""}`} data-testid="mla-card-changes-link">
          <GitCompare size={14} /> {t("see_changes_link")}
        </Link>
        {seat.tn1_name && (
          <Link to={`/c/${encodeURIComponent(seat.tn1_name)}`} className={`inline-flex items-center gap-2 rounded-full bg-[#f3eedc] px-4 py-2 text-[13px] font-semibold text-[#1a1640] hover:bg-[#ece6d3] transition-colors ${ta ? "font-tamil" : ""}`} data-testid="mla-card-candidates-link">
            {t("view_candidates")} <ArrowRight size={14} />
          </Link>
        )}
      </div>
    </section>
  );
}
